import { useQuery } from "@tanstack/vue-query";
import { adminUsersApi } from "@/api/adapters/admin/users";
import { makeAdminCustomerUser } from "@/models/internal/users";
import type { InternalCustomerUser } from "@/models/internal/users";
import { customRetry } from "@/plugins/vueQuery";
import { BAPI_COMMANDS } from "@/bapi-client/types/commands";
import { useBapi } from "@/bapi-client";

// Query keys for consistent caching
export const queryKeys = {
  users: {
    all: ["users"] as const,
    lists: () => [...queryKeys.users.all, "list"] as const,
    list: (filters: string) => [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    roles: () => [...queryKeys.users.all, "roles"] as const,
  },
} as const;

export function useGetAllAdminUsersQuery() {
  return useQuery<{ users: InternalCustomerUser[] }>({
    queryKey: queryKeys.users.lists(),
    queryFn: async (): Promise<{ users: InternalCustomerUser[] }> => {
      const data = await adminUsersApi.getAllUsers();

      if (data?.users) {
        return { users: data.users.map((user) => makeAdminCustomerUser(user)) };
      }
      return { users: [] };
    },
    retry: customRetry,
    staleTime: 2 * 60 * 1000, // 2 minutes for user lists
  });
}

export function useGetAdminUserQuery(userId: string) {
  return useQuery<{ user: InternalCustomerUser }>({
    queryKey: queryKeys.users.detail(userId),
    queryFn: async (): Promise<{ user: InternalCustomerUser }> => {
      const data = await adminUsersApi.getUser(userId);

      if (data?.user) {
        return { user: makeAdminCustomerUser(data.user) };
      }
      throw new Error("User not found");
    },
    enabled: !!userId,
    retry: customRetry,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual users
  });
}

// TODO: Convert to use the adminUsersApi adapter
export function useGetAdminUserRolesQuery() {
  return useQuery<{ roles: { id: number; name: string; description: string }[] }>({
    queryKey: queryKeys.users.roles(),
    queryFn: async (): Promise<{ roles: { id: number; name: string; description: string }[] }> => {
      const response = await useBapi(BAPI_COMMANDS.ADMIN_GET_USER_ROLES);
      if (response.success) {
        return { roles: response.data };
      }
      return { roles: [] };
    },
    retry: customRetry,
  });
}
